import React, { useMemo, useState } from 'react';
import { App, Button, Card, Col, Descriptions, Row, Space, Spin, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { PrintableHrmsTransaction } from '@/components/hr/PrintableHrmsTransaction';
import { ENTITY_TYPE_HR_LOAN } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';

const { Text } = Typography;

const LoanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions();
  const [loading, setLoading] = useState(false);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.loan(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getLoan(id!),
        () => hrmsService.listLoans(),
        id!
      ),
    enabled: Boolean(id),
  });

  const { data: schedule = [], isLoading: schLoading } = useQuery({
    queryKey: hrmsKeys.loanSchedule(id || ''),
    queryFn: async () => {
      try {
        return await hrmsService.getLoanSchedule(id!);
      } catch {
        return [];
      }
    },
    enabled: Boolean(id),
  });

  const workflow = useMemo(
    () => buildLinearWorkflow(['Draft', 'Pending', 'Active', 'Closed'], row?.status || 'Draft'),
    [row?.status]
  );

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Loan" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Loan" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const submit = async () => {
    setLoading(true);
    try {
      await hrmsService.submitLoan(id!);
      message.success('Submitted');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.loan(id!) });
    } catch {
      message.error('Submit failed');
    } finally {
      setLoading(false);
    }
  };

  const printDoc = () => {
    const scheduleLines = (Array.isArray(schedule) ? schedule : []).map((s: any, i: number) => ({
      description: `Installment ${s.installment_no ?? i + 1} (${String(s.period_month).padStart(2, '0')}/${s.period_year}) — ${s.status || '—'}`,
      value: String(s.due_amount ?? '—'),
    }));
    openHrmsPrintOverlay((onClose) => (
      <PrintableHrmsTransaction
        onClose={onClose}
        data={{
          docType: 'HR_LOAN',
          title: 'Employee Loan',
          documentNumber: row.loan_number,
          employee_name: empLabel(row.employee_id),
          status: row.status,
          meta: [
            { label: 'Type', value: String(row.loan_type || '—') },
            {
              label: 'Start',
              value: row.start_period_year
                ? `${String(row.start_period_month).padStart(2, '0')}/${row.start_period_year}`
                : '—',
            },
          ],
          lines: [
            { description: 'Principal', value: String(row.principal_amount ?? '—') },
            { description: 'Outstanding', value: String(row.outstanding_amount ?? '—') },
            { description: 'EMI', value: String(row.installment_amount ?? '—') },
            {
              description: 'Installments paid',
              value: `${row.installments_paid ?? 0}/${row.installments_total ?? 0}`,
            },
            ...scheduleLines,
          ],
          totals: [
            { label: 'Principal', value: String(row.principal_amount ?? '—') },
            { label: 'Outstanding', value: String(row.outstanding_amount ?? '—') },
          ],
          notes: row.notes,
        }}
      />
    ));
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Loan"
          documentNumber={row.loan_number  || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/loans')}>
                Back
              </Button>
              <Button icon={<PrinterOutlined />} onClick={printDoc}>
                Print
              </Button>
              {(row.status === 'Draft' || row.status === 'Pending') && (
                <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={submit}>
                  Submit
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Loan summary" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Type">{row.loan_type}</Descriptions.Item>
              <Descriptions.Item label="Principal">{row.principal_amount}</Descriptions.Item>
              <Descriptions.Item label="Outstanding">{row.outstanding_amount}</Descriptions.Item>
              <Descriptions.Item label="EMI">{row.installment_amount}</Descriptions.Item>
              <Descriptions.Item label="Installments">
                {row.installments_paid ?? 0}/{row.installments_total ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="Start">
                {row.start_period_year
                  ? `${String(row.start_period_month).padStart(2, '0')}/${row.start_period_year}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Notes">{row.notes || '—'}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="Installment schedule" style={{ marginBottom: 16 }}>
            <Table
              rowKey="id"
              size="small"
              loading={schLoading}
              dataSource={schedule}
              pagination={false}
              locale={{ emptyText: 'Schedule unavailable (BE GET /hr/loans/:id/schedule may be missing)' }}
              columns={[
                { title: '#', dataIndex: 'installment_no', width: 60 },
                {
                  title: 'Period',
                  key: 'p',
                  render: (_, r) => `${String(r.period_month).padStart(2, '0')}/${r.period_year}`,
                },
                { title: 'Due', dataIndex: 'due_amount', width: 110 },
                { title: 'Paid', dataIndex: 'paid_amount', width: 110 },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 110,
                  render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
                },
              ]}
            />
          </Card>
          <EntityAttachmentsCard entityType="HR_LOAN" entityId={row.id} />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_LOAN}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.loan(id!), hrmsKeys.loans()]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default LoanDetailPage;
